import {
  PaginationPrevAndNext,
  SurveyCakeLogicEngine
} from '@surveycake/logic-engine';
import {
  Equality,
  useEngineInstance,
  useEnginePagination,
  UseEnginePaginationSelector
} from '@surveycake/react-logic-engine';
import { useLatestValueRef } from '@surveycake/rhooks';
import { throttle } from '@surveycake/utils';
import {
  GoPageAndSubmitDisabledService,
  handleEngineValidation
} from 'app/modules/survey';
import React, { createContext, FC, useContext, useMemo } from 'react';

export type GoPage = () => void;
export type GoPageStatus = [boolean, boolean];
export interface IGoPageService extends FC {
  useGoPage: () => GoPage;
  useGoPageStatus: () => GoPageStatus;
}

const paginationEquality: Equality<PaginationPrevAndNext> = (prev, next) =>
  prev.enable === next.enable && prev.visible === next.visible;

/**
 * The factory of GoPageService.
 *
 * @param selector - The selector to get pagination data.
 * @param action - The goPage method implemantation.
 */
function createGoPageService(
  selector: UseEnginePaginationSelector<PaginationPrevAndNext>,
  action: (engine: SurveyCakeLogicEngine) => void
) {
  const GoPageContext = createContext<GoPage>(() => {});
  const GoPageStatusCOntext = createContext<GoPageStatus>([true, false]);

  function useGoPage() {
    const { enable, visible } = useEnginePagination(
      selector,
      paginationEquality
    );
    const disabled = GoPageAndSubmitDisabledService.useDisabled() || !enable;
    const disabledRef = useLatestValueRef(disabled);
    const engine = useEngineInstance();
    const goPage = useMemo(
      () =>
        throttle(400)(() => {
          if (!disabledRef.current) {
            action(engine);
          }
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [engine]
    );
    return [goPage, disabled, visible] as [() => void, boolean, boolean];
  }

  const GoPageService: IGoPageService = ({ children }) => {
    const [goPage, disabled, visible] = useGoPage();
    const statusDeps: GoPageStatus = [disabled, visible];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const status = useMemo<[boolean, boolean]>(() => statusDeps, statusDeps);

    return (
      <GoPageContext.Provider value={goPage}>
        <GoPageStatusCOntext.Provider value={status}>
          {children}
        </GoPageStatusCOntext.Provider>
      </GoPageContext.Provider>
    );
  };
  const useGoPageInner = () => useContext(GoPageContext);
  const useGoPageStatusInner = () => useContext(GoPageStatusCOntext);

  GoPageService.useGoPage = useGoPageInner;
  GoPageService.useGoPageStatus = useGoPageStatusInner;

  return GoPageService;
}

export const GoPrevPageService = createGoPageService(
  pagination => pagination.prev,
  /**
   * No need to validate while going to prev page.
   */
  engine => engine.goPrevPage()
);

export const GoNextPageService = createGoPageService(
  pagination => pagination.next,
  /**
   * Need to handle the validation result.
   */
  engine => {
    const validation = engine.goNextPage();

    if (validation) {
      handleEngineValidation(validation);
    }
  }
);
